import {formatDisplayDateTime} from '@/dayjs'
import {useTranslation} from '@/translations/i18n'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {AlertCircleIcon, ArrowLeftIcon, RefreshCw01Icon} from '@/components/ui/icon/Icon'
import {useGetBugReportIssueComments} from '../../hooks/useGetBugReportIssueComments'
import {BugReportIssue} from '../../types'
import {hasVisibleAssignee, IssueStatus} from './issueState'
import {Markdown} from './Markdown'
import {
    StyledCommentAuthor,
    StyledCommentContent,
    StyledCommentHeader,
    StyledCommentRow,
    StyledCommentsList,
    StyledCommentsState,
    StyledCommentTimestamp,
    StyledDetailAssignee,
    StyledDetailBar,
    StyledDetailHeading,
    StyledDetailMeta,
    StyledDetailNoAssignee,
    StyledDetailRoot,
    StyledDetailScroll,
    StyledDetailSection,
    StyledDetailSectionTitle,
    StyledDetailTitle,
    StyledEmptyBody,
    StyledHistoryError,
    StyledHistoryErrorState
} from './style'

interface BugReportIssueDetailProps {
    issue: BugReportIssue
    onBack: () => void
}

export const BugReportIssueDetail = ({issue, onBack}: BugReportIssueDetailProps) => {
    const {t} = useTranslation()
    const {
        data: comments,
        isLoading,
        isError,
        error,
        refetch,
        isFetching
    } = useGetBugReportIssueComments(issue.id, {enabled: true})
    const description = issue.description?.trim()
    const showAssignee = hasVisibleAssignee(issue)

    return (
        <StyledDetailRoot>
            <StyledDetailBar>
                <Button type="button" variant="ghost" size="sm" onClick={onBack}>
                    <ArrowLeftIcon size={16} />
                    {t('commons:back')}
                </Button>
                <IssueStatus issue={issue} />
            </StyledDetailBar>

            <StyledDetailScroll>
                <StyledDetailHeading>
                    <StyledDetailTitle>{issue.title}</StyledDetailTitle>
                    <StyledDetailMeta>
                        <span>{issue.identifier}</span>
                        <span>·</span>
                        <span>{formatDisplayDateTime(issue.createdAt)}</span>
                        <span>·</span>
                        {showAssignee && issue.assignee ? (
                            <>
                                <span>{t('bug_reporter:assignee_label')}</span>
                                <StyledDetailAssignee>
                                    <Avatar
                                        name={issue.assignee.name}
                                        imageUrl={issue.assignee.avatarUrl ?? undefined}
                                        size="xs"
                                    />
                                    {issue.assignee.name}
                                </StyledDetailAssignee>
                            </>
                        ) : (
                            <StyledDetailNoAssignee>{t('bug_reporter:assignee_empty')}</StyledDetailNoAssignee>
                        )}
                    </StyledDetailMeta>
                </StyledDetailHeading>

                <StyledDetailSection>
                    <StyledDetailSectionTitle>{t('bug_reporter:description_field_label')}</StyledDetailSectionTitle>
                    {description ? (
                        <Markdown content={description} />
                    ) : (
                        <StyledEmptyBody>{t('bug_reporter:no_description')}</StyledEmptyBody>
                    )}
                </StyledDetailSection>

                <StyledDetailSection>
                    <StyledDetailSectionTitle>
                        {t('bug_reporter:comments_section_title')}
                        {!!comments?.length && ` (${comments.length})`}
                    </StyledDetailSectionTitle>

                    {isLoading && (
                        <StyledCommentsState>
                            <Spinner size={16} />
                            {t('bug_reporter:comments_loading')}
                        </StyledCommentsState>
                    )}

                    {isError && (
                        <StyledHistoryErrorState>
                            <StyledHistoryError role="alert">
                                <AlertCircleIcon size={16} />
                                {error instanceof Error ? error.message : t('bug_reporter:comments_error')}
                            </StyledHistoryError>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => refetch()}
                                disabled={isFetching}
                            >
                                {isFetching ? <Spinner size={16} /> : <RefreshCw01Icon size={16} />}
                                {t('commons:retry')}
                            </Button>
                        </StyledHistoryErrorState>
                    )}

                    {!isLoading && !isError && !comments?.length && (
                        <StyledEmptyBody>{t('bug_reporter:comments_empty')}</StyledEmptyBody>
                    )}

                    {!isLoading && !isError && !!comments?.length && (
                        <StyledCommentsList>
                            {comments.map(comment => {
                                const authorName = comment.user?.name || t('bug_reporter:comments_unknown_author')
                                return (
                                    <StyledCommentRow key={comment.id}>
                                        <Avatar
                                            size="xs"
                                            name={authorName}
                                            imageUrl={comment.user?.avatarUrl ?? undefined}
                                        />
                                        <StyledCommentContent>
                                            <StyledCommentHeader>
                                                <StyledCommentAuthor>{authorName}</StyledCommentAuthor>
                                                <StyledCommentTimestamp>
                                                    {formatDisplayDateTime(comment.createdAt)}
                                                </StyledCommentTimestamp>
                                            </StyledCommentHeader>
                                            <Markdown content={comment.body} />
                                        </StyledCommentContent>
                                    </StyledCommentRow>
                                )
                            })}
                        </StyledCommentsList>
                    )}
                </StyledDetailSection>
            </StyledDetailScroll>
        </StyledDetailRoot>
    )
}
